import React, { forwardRef, useContext } from 'react';
import _ from 'lodash';

import './VirtualKeyboard.scss';
import VirtualKeyboardContext from './Context/VirtualKeyboard.context';
import Icon from '../Icon/Icon';
import classNames from 'classnames';

interface VirtualKeyboardProps {
  /**
   * Any HTML element placed inside VirtualKeyboard
   */
  children?: React.ReactNode,
  specialCharacter?: '.' | '+',
  noPadding?: boolean
}

const VirtualKeyboard = forwardRef<HTMLDivElement, VirtualKeyboardProps>(({ children,
  specialCharacter = '.',
  noPadding=false
}: VirtualKeyboardProps, ref) => {
  const { showKeyboard, setShowKeyboard, selectedInput } = useContext(VirtualKeyboardContext);

  // THIS FUNCTION CALLS NATIVE VALUE SETTER
  const setNativeValue = (element, value) => {
    const valueSetter = Object.getOwnPropertyDescriptor(element, 'value').set;
    const prototype = Object.getPrototypeOf(element);
    const prototypeValueSetter = Object.getOwnPropertyDescriptor(prototype, 'value').set;

    if (valueSetter && valueSetter !== prototypeValueSetter) {
      prototypeValueSetter.call(element, value);
    } else {
      valueSetter.call(element, value);
    }
  };

  const sum = (value?: string) => (e) => {
    e.preventDefault();

    if (!_.isNull(selectedInput)) {
      // Only allow a single dot.
      if (value === '.' && _.includes(selectedInput.value, '.')) {
        return;
      }

      // Prevent more than 2 decimals.
      if (_.includes(selectedInput.value, '.') && selectedInput.value.split('.')[1].length === 2 && value) {
        return;
      }

      let newValue = '';
      if (value) {
        if (value === '-') {
          // Delete: Removes last char.
          newValue = selectedInput.value.slice(0, -1);
        } else {
          newValue = selectedInput.value === '0' && specialCharacter === '.' ? value : selectedInput.value + value;
        }
      } else if (selectedInput.value === '') {
        // Prevent clearing value already empty to avoid side-effects with previous value.
        return;
      }

      setNativeValue(selectedInput, newValue);
      selectedInput.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };

  return (
    <>
      { !!children && !showKeyboard && children }
      {
        showKeyboard && (
          <div
            className={
              classNames({
                'virtual-keyboard': true,
                'virtual-keyboard--no-padding': noPadding
              })
            }
            ref={ ref }
          >
            <div className="virtual-keyboard__numpad">

              <div onClick={ sum('1') }>1</div>
              <div onClick={ sum('2') }>2</div>
              <div onClick={ sum('3') }>3</div>
              <div onClick={ sum('4') }>4</div>
              <div onClick={ sum('5') }>5</div>
              <div onClick={ sum('6') }>6</div>

              <div className="virtual-keyboard__close">
                {
                  !noPadding &&
                <Icon icon="close" onClick={ () => setShowKeyboard(false) } />
                }
              </div>
              <div onClick={ sum('7') }>7</div>
              <div onClick={ sum('8') }>8</div>
              <div onClick={ sum('9') }>9</div>
              <div onClick={ sum('0') }>0</div>
              <div onClick={ sum(specialCharacter) }>{ specialCharacter }</div>
              <div onClick={ sum('-') }><Icon icon="backArrow" /></div>
              <div onClick={ sum() }>AC</div>
            </div>
          </div>
        )
      }
    </>
  );
});

VirtualKeyboard.displayName = 'VirtualKeyboard'; // workaround due to displayName lint error.

export default VirtualKeyboard;
